import {
  InfoEvent,
  BidderEvent,
  LabradorEvent,
  LoadTimeEvent,
  SizeInfoEvent,
  TrackingEvent,
  IdentityInfoEvent,
  type AdInfoTrackDataUnion,
  type AdEngBiddersTrackData,
  type LabradorTrackData,
  type TrackingTrackData,
  type TrackDataUnion,
  type LoadTimeTrackData,
  type AdSizesTrackData,
  type IdentityInfoTrackData,
} from "common-types";

/**
 * Helper function that allows for checking enums
 */
function isEnumValue<T extends Record<string, string>>(
  enumObj: T,
  value: string,
): value is T[keyof T] {
  return Object.values(enumObj).includes(value);
}

/**
 *
 * When reading data out of `window.fandomTrack.q`, we need to be able to differentiate
 * which data types are which, so that they can be processed properly.
 *
 * These functions will also be used as type guards so that once events are identified their
 * type can be narrowed. Today we do not have more specific TrackData types; that will follow
 * in a subsequent PR, and the `data is FandomTrackData` statements will be updated to the correct type.
 */

/**
 * Typeguard for the AdInfoTrackDataUnion
 */
export function isInfoEvent(
  data: TrackDataUnion,
): data is AdInfoTrackDataUnion {
  return isEnumValue(InfoEvent, data.event);
}

/**
 * Typeguard for the AdEngBiddersTrackData
 */
export function isBidderEvent(
  data: TrackDataUnion,
): data is AdEngBiddersTrackData {
  return isEnumValue(BidderEvent, data.event);
}

/**
 * Typeguard for the LabradorTrackData
 */
export function isLabradorEvent(
  data: TrackDataUnion,
): data is LabradorTrackData {
  return isEnumValue(LabradorEvent, data.event);
}

/**
 * Typeguard for the IdentityInfoTrackData
 */
export function isIdentityInfoEvent(
  data: TrackDataUnion,
): data is IdentityInfoTrackData {
  return isEnumValue(IdentityInfoEvent, data.event);
}

/**
 * Typeguard for the LoadTimeTrackData
 */
export function isLoadTimeEvent(
  data: TrackDataUnion,
): data is LoadTimeTrackData {
  return isEnumValue(LoadTimeEvent, data.event);
}

/**
 * Typeguard for the SizeInfoData
 */
export function isSizeInfoEvent(
  data: TrackDataUnion,
): data is AdSizesTrackData {
  return isEnumValue(SizeInfoEvent, data.event);
}

/**
 * Typeguard for the TrackingTrackData
 */
export function isTrackingEvent(
  data: TrackDataUnion,
): data is TrackingTrackData {
  return isEnumValue(TrackingEvent, data.event);
}
